import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    Box,
    Button,
    FormField,
    Container,
    SpaceBetween,
    Header,
    Select,
    Textarea,
    Input,
    StatusIndicator,
    Alert,
    SelectProps,
    Spinner
} from "@amzn/awsui-components-react-v3";
import { v4 as uuidv4 } from 'uuid';
import { getUserAlias } from "src/utils/cookie-helper";
import { WorkitemManager } from "src/model/WorkitemManager";
import { buildTicketPayload, DropdownOption, PillarResponse, CTIConfig, WorkItemDetails } from "./utils/ticket-utils";
import {
    pillarOptions,
    ERROR_MESSAGES,
    SUCCESS_MESSAGES,
    FORM_LABELS,
    PLACEHOLDERS,
    BUTTON_LABELS,
    ALERT_HEADERS,
    TicketPayload
} from "src/constants/wim";
import { BaseChangeDetail } from "@amzn/awsui-components-react-v3/polaris/input/interfaces";
import { NonCancelableCustomEvent } from "@amzn/awsui-components-react-v3/polaris";

export function CreateAdhocTicket(): JSX.Element {
    const [pillar, setPillar] = useState<DropdownOption | null>(null);
    const [system, setSystem] = useState<DropdownOption | null>(null);
    const [workItemType, setWorkItemType] = useState<DropdownOption | null>(null);
    const [capabilityPrimitive, setCapabilityPrimitive] = useState<DropdownOption | null>(null);
    const [processPrimitive, setProcessPrimitive] = useState<DropdownOption | null>(null);
    const [program, setProgram] = useState<DropdownOption | null>(null);
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [selectedCTIConfig, setSelectedCTIConfig] = useState<string | null>(null);

    const workitemManager = useMemo(() => new WorkitemManager(), []);

    const { data: pillarData, isLoading: loadingPillarData } = useQuery<PillarResponse, Error>({
        queryKey: ['pillarDetails', pillar?.value],
        queryFn: async () => {
            const response = await workitemManager.getPillarDetails(pillar!.value);
            return response.data;
        },
        enabled: !!pillar?.value
    });

    const { data: ctiConfig, isLoading: loadingCTIConfig } = useQuery<CTIConfig, Error>({
        queryKey: ['ctiConfig', selectedCTIConfig],
        queryFn: async () => {
            const response = await workitemManager.getWorkItemCTI(selectedCTIConfig!);
            return response.data;
        },
        enabled: !!selectedCTIConfig
    });

    const filteredSystemOptions = useMemo(() => {
        if (!pillarData?.pillarDetails || !pillar?.value) return [];

        return Object.keys(pillarData.pillarDetails[pillar.value]).map(systemName => ({
            value: systemName,
            label: systemName
        }));
    }, [pillar, pillarData]);

    const filteredWorkItemOptions = useMemo(() => {
        if (!pillarData?.pillarDetails || !pillar?.value || !system?.value) return [];

        const workItems = pillarData.pillarDetails[pillar.value][system.value];
        return workItems?.map((item: WorkItemDetails) => ({
            value: item.workitemType,
            label: item.workitemType
        })) ?? [];
    }, [pillar, system, pillarData]);

    useEffect(() => {
        setSystem(null);
        setWorkItemType(null);
        setCapabilityPrimitive(null);
        setProcessPrimitive(null);
        setProgram(null);
        setSelectedCTIConfig(null);
    }, [pillar]);

    useEffect(() => {
        setWorkItemType(null);
        setCapabilityPrimitive(null);
        setProcessPrimitive(null);
        setProgram(null);
        setSelectedCTIConfig(null);
    }, [system]);

    const resetForm = useCallback(() => {
        setPillar(null);
        setSystem(null);
        setWorkItemType(null);
        setCapabilityPrimitive(null);
        setProcessPrimitive(null);
        setProgram(null);
        setTitle("");
        setDescription("");
        setSelectedCTIConfig(null);
    }, []);

    const createWorkItem = async (payload: TicketPayload) => {
        return workitemManager.createWorkitem(payload);
    };

    const createTicketMutation = useMutation({
        mutationFn: createWorkItem,
        onSuccess: () => resetForm(),
        onError: (error) => {
            console.error(`${ERROR_MESSAGES.CREATE_TICKET_FAILURE}:`, error);
        }
    });

    const handleWorkItemTypeChange = useCallback((event: { detail: SelectProps.ChangeDetail }) => {
        const selectedWorkItem = event.detail.selectedOption as DropdownOption;
        setWorkItemType(selectedWorkItem);

        if (pillar?.value && system?.value && pillarData?.pillarDetails) {
            const workItems: WorkItemDetails[] = pillarData.pillarDetails[pillar.value][system.value];
            const workItemDetail = workItems.find((item: WorkItemDetails) =>
                item.workitemType === selectedWorkItem.value
            );

            if (workItemDetail) {
                setCapabilityPrimitive({
                    value: workItemDetail.capabilityPrimitive,
                    label: workItemDetail.capabilityPrimitive
                });
                setProcessPrimitive({
                    value: workItemDetail.processPrimitive,
                    label: workItemDetail.processPrimitive
                });
                setProgram({
                    value: workItemDetail.program,
                    label: workItemDetail.program
                });

                setSelectedCTIConfig(workItemDetail.ctiConfig);
            }
        }
    }, [pillar, system, pillarData]);

    const createTicket = useCallback(async (): Promise<void> => {
        const username = getUserAlias();
        const workItemId = uuidv4();
        const contextId = uuidv4();

        if (!ctiConfig) {
            return;
        }

        const workItemTypeDetails = {
            category: ctiConfig.category,
            type: ctiConfig.type,
            item: ctiConfig.item,
            resolverGroup: ctiConfig.resolverGroup,
            simFolderId: ctiConfig.simFolderId
        };

        const ticketPayload = buildTicketPayload(
            workItemId,
            contextId,
            username,
            title,
            description,
            pillar?.value,
            system?.value,
            workItemType?.value,
            capabilityPrimitive?.value,
            processPrimitive?.value,
            program?.value,
            workItemTypeDetails
        );

        createTicketMutation.mutate(ticketPayload);
    }, [
        workItemType, title, description, pillar, system,
        capabilityPrimitive, processPrimitive, program, createTicketMutation,
        ctiConfig
    ]);

    const validateForm = useCallback((): boolean => {
        return Boolean(
            pillar?.value &&
            system?.value &&
            workItemType?.value &&
            title &&
            ctiConfig
        );
    }, [pillar, system, workItemType, title, ctiConfig]);

    return (
        <Box>
            <Container header={<Header variant="h2">Create Workitem</Header>}>
                <form onSubmit={(e: React.FormEvent) => { e.preventDefault(); }}>
                    <SpaceBetween direction="vertical" size="l">
                        {createTicketMutation.isError && (
                            <Alert data-testid="error-message" type="error" header={ALERT_HEADERS.ERROR}>
                                {`${ERROR_MESSAGES.CREATE_TICKET_FAILURE}: ${
                                    createTicketMutation.error instanceof Error
                                        ? createTicketMutation.error.message
                                        : ERROR_MESSAGES.UNKNOWN_ERROR
                                }`}
                            </Alert>
                        )}

                        {createTicketMutation.isSuccess && (
                            <Alert data-testid="success-message" type="success" header={ALERT_HEADERS.SUCCESS}>
                                <StatusIndicator type="success">
                                    {SUCCESS_MESSAGES.TICKET_CREATED}
                                </StatusIndicator>
                            </Alert>
                        )}

                        <FormField label={FORM_LABELS.TITLE}>
                            <Input
                                data-testid="title-input"
                                value={title}
                                onChange={(event: NonCancelableCustomEvent<BaseChangeDetail>) => setTitle(event.detail.value)}
                                placeholder={PLACEHOLDERS.TITLE}
                            />
                        </FormField>

                        <FormField label={FORM_LABELS.DESCRIPTION}>
                            <Textarea
                                data-testid="description-textarea"
                                value={description}
                                onChange={(event: NonCancelableCustomEvent<BaseChangeDetail>) => setDescription(event.detail.value)}
                                placeholder={PLACEHOLDERS.DESCRIPTION}
                                rows={3}
                            />
                        </FormField>

                        <FormField label={FORM_LABELS.PILLAR}>
                            <Select
                                data-testid="pillar-select"
                                selectedOption={pillar}
                                onChange={(event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) =>
                                    setPillar(event.detail.selectedOption as DropdownOption)
                                }
                                options={pillarOptions}
                                placeholder={PLACEHOLDERS.PILLAR}
                                filteringType="auto"
                            />
                        </FormField>

                        <FormField label={FORM_LABELS.SYSTEM}>
                            {pillar && loadingPillarData ? (
                                <Spinner size="normal" />
                            ) : (
                                <Select
                                    data-testid="system-select"
                                    selectedOption={system}
                                    onChange={(event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) =>
                                        setSystem(event.detail.selectedOption as DropdownOption)
                                    }
                                    options={filteredSystemOptions}
                                    placeholder={PLACEHOLDERS.SYSTEM}
                                    filteringType="auto"
                                    disabled={!pillar || loadingPillarData}
                                />
                            )}
                        </FormField>

                        <FormField label={FORM_LABELS.WORK_ITEM_TYPE}>
                            {system && loadingPillarData ? (
                                <Spinner size="normal" />
                            ) : (
                                <Select
                                    data-testid="workitem-type-select"
                                    selectedOption={workItemType}
                                    onChange={handleWorkItemTypeChange}
                                    options={filteredWorkItemOptions}
                                    placeholder={PLACEHOLDERS.WORK_ITEM_TYPE}
                                    filteringType="auto"
                                    disabled={!system || loadingPillarData}
                                />
                            )}
                        </FormField>

                        <FormField label={FORM_LABELS.CAPABILITY_PRIMITIVE}>
                            <Select
                                data-testid="capability-primitive-select"
                                selectedOption={capabilityPrimitive}
                                options={[capabilityPrimitive].filter(Boolean) as DropdownOption[]}
                                placeholder={PLACEHOLDERS.CAPABILITY}
                                filteringType="auto"
                                disabled={!workItemType}
                            />
                        </FormField>

                        <FormField label={FORM_LABELS.PROCESS_PRIMITIVE}>
                            <Select
                                data-testid="process-primitive-select"
                                selectedOption={processPrimitive}
                                options={[processPrimitive].filter(Boolean) as DropdownOption[]}
                                placeholder={PLACEHOLDERS.PROCESS}
                                filteringType="auto"
                                disabled={!workItemType}
                            />
                        </FormField>

                        <FormField label={FORM_LABELS.PROGRAM}>
                            <Select
                                data-testid="program-select"
                                selectedOption={program}
                                options={[program].filter(Boolean) as DropdownOption[]}
                                placeholder={PLACEHOLDERS.PROGRAM}
                                filteringType="auto"
                                disabled={!workItemType}
                            />
                        </FormField>

                        {selectedCTIConfig && loadingCTIConfig && (
                            <SpaceBetween direction="horizontal" size="xs">
                                <Spinner size="normal" />
                                <StatusIndicator type="info">Loading CTI configuration...</StatusIndicator>
                            </SpaceBetween>
                        )}

                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="link"
                                    onClick={resetForm}
                                    disabled={createTicketMutation.isLoading}
                                >
                                    {BUTTON_LABELS.CANCEL}
                                </Button>
                                <Button
                                    data-testid="create-ticket-button"
                                    variant="primary"
                                    onClick={createTicket}
                                    disabled={!validateForm() || createTicketMutation.isLoading || loadingCTIConfig}
                                    loading={createTicketMutation.isLoading}
                                >
                                    {BUTTON_LABELS.CREATE_TICKET}
                                </Button>
                            </SpaceBetween>
                        </Box>
                    </SpaceBetween>
                </form>
            </Container>
        </Box>
    );
}